import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  buscarProduto,
  buscarDicionarioTemplate,
  salvarMeta,
  salvarEnrich,
  sugerirMedicamentoIA,
  uploadImagem,
  atualizarImagem,
  removerImagem,
  reordenarImagens,
  gerarDescricaoIA,
  listarAncoras,
  vincularMembro,
  removerMembro,
  type MetaInput,
  type EnrichInput,
  type FieldFonte,
  type AncoraResumo,
  type DicionarioEntry,
} from './api'

// Fallback estático do template (espelho do seed da migration 013) — usado só até o
// GET /dicionario/template responder (ou se ele falhar). A fonte viva é o banco.
const FALLBACK_TEMPLATE: DicionarioEntry[] = [
  { chave: 'principio_ativo', rotulo: 'Princípio ativo', tipo: 'texto', destino: 'coluna', coluna_fisica: 'principio_ativo', enum_valores: null, sinonimos: null, ordem: 10 },
  { chave: 'concentracao', rotulo: 'Concentração', tipo: 'texto', destino: 'coluna', coluna_fisica: 'concentracao', enum_valores: null, sinonimos: null, ordem: 11 },
  { chave: 'forma_farmaceutica', rotulo: 'Forma farmacêutica', tipo: 'texto', destino: 'coluna', coluna_fisica: 'forma_farmaceutica', enum_valores: null, sinonimos: null, ordem: 12 },
  { chave: 'via_administracao', rotulo: 'Via de administração', tipo: 'texto', destino: 'coluna', coluna_fisica: 'via_administracao', enum_valores: null, sinonimos: null, ordem: 13 },
  { chave: 'apresentacao', rotulo: 'Apresentação', tipo: 'texto', destino: 'coluna', coluna_fisica: 'apresentacao', enum_valores: null, sinonimos: null, ordem: 14 },
  { chave: 'marca', rotulo: 'Marca', tipo: 'texto', destino: 'coluna', coluna_fisica: 'marca', enum_valores: null, sinonimos: null, ordem: 20 },
  { chave: 'fabricante', rotulo: 'Fabricante', tipo: 'texto', destino: 'coluna', coluna_fisica: 'fabricante', enum_valores: null, sinonimos: null, ordem: 21 },
  { chave: 'categoria', rotulo: 'Categoria', tipo: 'texto', destino: 'coluna', coluna_fisica: 'categoria', enum_valores: null, sinonimos: null, ordem: 30 },
  { chave: 'subcategoria', rotulo: 'Subcategoria', tipo: 'texto', destino: 'coluna', coluna_fisica: 'subcategoria', enum_valores: null, sinonimos: null, ordem: 31 },
  { chave: 'especie', rotulo: 'Espécie', tipo: 'texto', destino: 'coluna', coluna_fisica: 'especie', enum_valores: ['caes', 'gatos', 'equinos', 'bovinos'], sinonimos: null, ordem: 40 },
  { chave: 'fase_vida', rotulo: 'Fase de vida', tipo: 'texto', destino: 'coluna', coluna_fisica: 'fase_vida', enum_valores: ['filhote', 'adulto', 'senior'], sinonimos: null, ordem: 42 },
  { chave: 'porte', rotulo: 'Porte', tipo: 'texto', destino: 'coluna', coluna_fisica: 'porte', enum_valores: ['pequeno', 'medio', 'medio_grande', 'grande'], sinonimos: null, ordem: 43 },
  { chave: 'peso_min', rotulo: 'Peso mín. do animal (kg)', tipo: 'numero', destino: 'coluna', coluna_fisica: 'peso_min', enum_valores: null, sinonimos: null, ordem: 60 },
  { chave: 'peso_max', rotulo: 'Peso máx. do animal (kg)', tipo: 'numero', destino: 'coluna', coluna_fisica: 'peso_max', enum_valores: null, sinonimos: null, ordem: 61 },
]

export default function ProdutoDetalhe() {
  const { codigo = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['produto', codigo],
    queryFn: () => buscarProduto(codigo),
    enabled: !!codigo,
  })

  if (isLoading) return <p className="p-8 text-slate-500">Carregando…</p>
  if (error)
    return (
      <p className="p-8 text-red-600">
        Erro: {error instanceof Error ? error.message : String(error)}
      </p>
    )
  if (!data) return null

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6 space-y-6">
      <div>
        {/* Volta no histórico (preserva filtros da lista na URL); fallback pra "/" se entrou direto */}
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          className="text-sm text-blue-700 hover:underline"
        >
          ← Voltar para a lista
        </button>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">{data.produto.nome}</h1>
        <p className="text-sm text-slate-500 font-mono">
          SKU {codigo} · R$ {data.produto.preco_venda} · estoque {data.produto.estoque_atual}
          {data.produto.departamento && ` · ${data.produto.departamento}`}
          {data.produto.categoria_nome && ` / ${data.produto.categoria_nome}`}
        </p>
      </div>

      <ErpReadonly data={data} />
      {/* Ordem do fluxo: cure os FATOS primeiro (EnrichForm), depois gere a
          DESCRIÇÃO (MetaForm) — a descrição ancora a redação nos fatos curados. */}
      <EnrichForm
        codigo={codigo}
        enrich={data.enrich}
        onSaved={() => qc.invalidateQueries({ queryKey: ['produto', codigo] })}
      />
      <MetaForm
        codigo={codigo}
        atual={data}
        onSaved={() => qc.invalidateQueries({ queryKey: ['produto', codigo] })}
      />
      <EquivalenciaBloco
        codigo={codigo}
        equivalencia={data.equivalencia}
        onChange={() => qc.invalidateQueries({ queryKey: ['produto', codigo] })}
      />
      <ImagensBloco
        codigo={codigo}
        imagens={data.imagens}
        onChange={() => qc.invalidateQueries({ queryKey: ['produto', codigo] })}
      />
    </div>
  )
}

function EquivalenciaBloco({
  codigo,
  equivalencia,
  onChange,
}: {
  codigo: string
  equivalencia: ProdutoDetalheData['equivalencia']
  onChange: () => void
}) {
  const [busca, setBusca] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebounced(busca.trim()), 300)
    return () => clearTimeout(t)
  }, [busca])

  const ancorasQ = useQuery({
    queryKey: ['ancoras-busca', debounced],
    queryFn: () => listarAncoras(debounced),
    enabled: debounced.length >= 2,
  })

  const mVincular = useMutation({
    mutationFn: (ancoraId: string) => vincularMembro(codigo, ancoraId),
    onSuccess: () => { setBusca(''); onChange() },
  })
  const mRemover = useMutation({
    mutationFn: () => removerMembro(codigo),
    onSuccess: onChange,
  })

  const vinculadoAncora = equivalencia?.ancora_id != null
  const semEquivalente = !!equivalencia?.sem_correlacao

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <h2 className="text-sm font-semibold text-slate-700 mb-1">Equivalência</h2>
      <p className="text-xs text-slate-500 mb-3">
        Associe este produto a uma âncora genérica para somar estoque e comprar em conjunto.
      </p>

      {vinculadoAncora ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <span className="text-sm text-emerald-900">
            Vinculado à âncora{' '}
            <Link to={`/equivalencia/${equivalencia!.ancora_id}`} className="font-semibold underline">
              {equivalencia!.nome}
            </Link>{' '}
            <span className="font-mono text-xs text-emerald-700">({equivalencia!.codigo_compra})</span>
          </span>
          <button
            onClick={() => mRemover.mutate()}
            disabled={mRemover.isPending}
            className="ml-auto text-xs text-red-600 hover:underline disabled:opacity-40"
          >
            remover vínculo
          </button>
        </div>
      ) : semEquivalente ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <span className="text-sm text-slate-600">Marcado como <strong>sem equivalente</strong>.</span>
          <button
            onClick={() => mRemover.mutate()}
            disabled={mRemover.isPending}
            className="ml-auto text-xs text-blue-700 hover:underline disabled:opacity-40"
          >
            desfazer
          </button>
        </div>
      ) : (
        <div>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar âncora por nome ou código (EQ…)…"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {debounced.length >= 2 && (
            <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
              {ancorasQ.isLoading && <p className="px-4 py-3 text-xs text-slate-400">Buscando…</p>}
              {ancorasQ.data?.length === 0 && (
                <p className="px-4 py-3 text-xs text-slate-400">Nenhuma âncora encontrada.</p>
              )}
              {ancorasQ.data?.map((a: AncoraResumo) => (
                <div key={a.id} className="flex items-center gap-3 px-4 py-2 border-t border-slate-100 first:border-t-0">
                  <span className="font-mono text-xs text-slate-500 w-20">{a.codigo_compra}</span>
                  <span className="flex-1 text-sm text-slate-800">{a.nome}</span>
                  <span className="text-xs text-slate-400">{a.qtd_membros} membro(s)</span>
                  <button
                    onClick={() => mVincular.mutate(a.id)}
                    disabled={mVincular.isPending}
                    className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                  >
                    vincular
                  </button>
                </div>
              ))}
            </div>
          )}
          {mVincular.error && (
            <p className="mt-2 text-xs text-red-600">
              {mVincular.error instanceof Error ? mVincular.error.message : 'Erro ao vincular'}
            </p>
          )}
        </div>
      )}
    </section>
  )
}

function ErpReadonly({ data }: { data: ProdutoDetalheData }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
        Dados do ERP (somente leitura)
      </h2>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <Field label="Marca ERP" value={data.produto.marca || '—'} />
        <Field label="Grupo ERP" value={data.produto.grupo || '—'} />
        <Field label="Peso ERP" value={data.produto.peso ? `${data.produto.peso} kg` : '—'} />
        <Field label="Atualizado em" value={new Date(data.produto.atualizado_em).toLocaleString('pt-BR')} />
      </dl>
      {data.produto.descricao && (
        <div className="mt-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Descrição ERP</p>
          <p className="text-sm text-slate-700">{data.produto.descricao}</p>
        </div>
      )}
    </section>
  )
}

function MetaForm({
  codigo,
  atual,
  onSaved,
}: {
  codigo: string
  atual: ProdutoDetalheData
  onSaved: () => void
}) {
  const [form, setForm] = useState<MetaInput>({
    descricao_longa: atual.produto.descricao_longa,
    bullets: atual.produto.bullets ?? [],
    marca_override: atual.produto.marca_override,
    peso: atual.produto.peso_override ? Number(atual.produto.peso_override) : null,
    altura: atual.produto.altura,
    largura: atual.produto.largura,
    comprimento: atual.produto.comprimento,
  })

  useEffect(() => {
    setForm({
      descricao_longa: atual.produto.descricao_longa,
      bullets: atual.produto.bullets ?? [],
      marca_override: atual.produto.marca_override,
      peso: atual.produto.peso_override ? Number(atual.produto.peso_override) : null,
      altura: atual.produto.altura,
      largura: atual.produto.largura,
      comprimento: atual.produto.comprimento,
    })
  }, [atual])

  const mut = useMutation({
    mutationFn: () =>
      salvarMeta(codigo, {
        ...form,
        bullets: form.bullets && form.bullets.length > 0 ? form.bullets.filter(Boolean) : null,
      }),
    onSuccess: onSaved,
  })

  const ia = useMutation({
    mutationFn: () => gerarDescricaoIA(codigo),
    onSuccess: (r) => {
      // Substitui descrição e bullets atuais pelos sugeridos.
      // O usuário pode editar antes de salvar.
      setForm((f) => ({
        ...f,
        descricao_longa: r.descricao,
        bullets: r.bullets,
      }))
    },
  })

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          <span className="text-blue-700">Passo 2 · </span>
          Enriquecimento (descrição rica, marca, dimensões)
        </h2>
        <div className="flex items-center gap-3">
          {ia.isSuccess && (
            <span className="text-xs text-blue-600">
              ✦ IA gerou (revise antes de salvar)
            </span>
          )}
          {ia.isError && (
            <span className="text-xs text-red-600">
              IA: {String((ia.error as Error).message)}
            </span>
          )}
          <button
            type="button"
            onClick={() => ia.mutate()}
            disabled={ia.isPending}
            className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-800 font-medium px-3 py-1 rounded-full transition disabled:opacity-50"
          >
            {ia.isPending ? 'gerando…' : '✦ Gerar com IA'}
          </button>
          {mut.isSuccess && <span className="text-xs text-green-600">✓ salvo</span>}
          {mut.isError && (
            <span className="text-xs text-red-600">erro: {String((mut.error as Error).message)}</span>
          )}
        </div>
      </div>

      <p className="text-xs text-blue-700 mb-4">
        A IA ancora a redação nos <span className="font-semibold">fatos curados do Passo 1</span> (acima).
        Cure os fatos primeiro para um texto mais preciso e seguro.
      </p>

      <div className="space-y-4">
        <Textarea
          label="Descrição rica (markdown)"
          rows={6}
          value={form.descricao_longa ?? ''}
          onChange={(v) => setForm((f) => ({ ...f, descricao_longa: v || null }))}
          placeholder="Texto longo que aparece na página do produto. Aceita markdown."
        />

        <BulletsEditor
          bullets={form.bullets ?? []}
          onChange={(b) => setForm((f) => ({ ...f, bullets: b }))}
        />

        <Input
          label="Marca (override do ERP — só preencha se ERP vier vazio/errado)"
          value={form.marca_override ?? ''}
          onChange={(v) => setForm((f) => ({ ...f, marca_override: v || null }))}
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NumberInput
            label="Peso (kg)"
            value={form.peso}
            onChange={(v) => setForm((f) => ({ ...f, peso: v }))}
          />
          <NumberInput
            label="Altura (cm)"
            value={form.altura}
            onChange={(v) => setForm((f) => ({ ...f, altura: v }))}
          />
          <NumberInput
            label="Largura (cm)"
            value={form.largura}
            onChange={(v) => setForm((f) => ({ ...f, largura: v }))}
          />
          <NumberInput
            label="Comprimento (cm)"
            value={form.comprimento}
            onChange={(v) => setForm((f) => ({ ...f, comprimento: v }))}
          />
        </div>

        <div className="flex justify-end pt-2 border-t border-slate-100">
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="bg-blue-700 hover:bg-blue-800 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {mut.isPending ? 'Salvando…' : 'Salvar enriquecimento'}
          </button>
        </div>
      </div>
    </section>
  )
}

function BulletsEditor({
  bullets,
  onChange,
}: {
  bullets: string[]
  onChange: (b: string[]) => void
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-slate-500 mb-2">
        Bullets (3-5 frases curtas de benefícios)
      </label>
      <div className="space-y-2">
        {bullets.map((b, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={b}
              onChange={(e) => {
                const novo = [...bullets]
                novo[i] = e.target.value
                onChange(novo)
              }}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              placeholder={`Bullet ${i + 1}`}
            />
            <button
              type="button"
              onClick={() => onChange(bullets.filter((_, j) => j !== i))}
              className="px-3 text-slate-400 hover:text-red-600"
              aria-label="Remover"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...bullets, ''])}
          className="text-xs text-blue-700 hover:underline"
        >
          + Adicionar bullet
        </button>
      </div>
    </div>
  )
}

function ProvenanceBadge({ enrich }: { enrich: ProdutoDetalheData['enrich'] }) {
  const fonte = enrich?.fonte_classificacao
  const map: Record<string, string> = {
    manual: 'bg-green-100 text-green-800',
    ia: 'bg-purple-100 text-purple-800',
    deterministico: 'bg-slate-100 text-slate-600',
  }
  if (!fonte) return <span className="text-xs text-slate-400">sem classificação</span>
  return (
    <span className="flex items-center gap-2">
      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${map[fonte] ?? 'bg-slate-100'}`}>
        {fonte}
      </span>
      {enrich?.revisado_em && (
        <span className="text-[11px] text-slate-400">
          revisado por {enrich.revisado_por} · {new Date(enrich.revisado_em).toLocaleDateString('pt-BR')}
        </span>
      )}
    </span>
  )
}

// Cor por fonte (rank do gate aplicar_fato: manual > fabricante > ml > ia > deterministico > erp).
const FONTE_COR: Record<string, string> = {
  manual: 'bg-green-100 text-green-700',
  fabricante: 'bg-amber-100 text-amber-700',
  ml: 'bg-blue-100 text-blue-700',
  ia: 'bg-purple-100 text-purple-700',
  deterministico: 'bg-slate-100 text-slate-500',
  erp: 'bg-slate-100 text-slate-500',
}

// Badge de proveniência POR-CAMPO (ADR-0024): lê produto_enrich.fontes[campo]. Mostra quem
// gravou aquele fato; '✎' = curadoria humana (intocável por automação). Tombstone = campo
// limpo por humano (automação não ressuscita). Tooltip com fonte + data.
function FieldBadge({ f }: { f?: FieldFonte }) {
  if (!f) return null
  const data = f.em ? new Date(f.em).toLocaleDateString('pt-BR') : null
  if (f.deletado) {
    return (
      <span title={`removido por curadoria humana${data ? ` · ${data}` : ''}`}
        className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
        ✎ limpo
      </span>
    )
  }
  const titulo = `${f.humano ? 'curadoria humana' : 'automação'} · fonte ${f.fonte}${data ? ` · ${data}` : ''}`
  return (
    <span title={titulo}
      className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full ${FONTE_COR[f.fonte] ?? 'bg-slate-100 text-slate-500'}`}>
      {f.humano ? '✎ ' : ''}{f.fonte}
    </span>
  )
}

// Seção de revisão do enriquecimento central (ADR 0023). Salvar marca como curadoria
// humana (manual) — fonte de verdade dos fatos genéricos (princípio ativo, ficha, taxonomia).
function EnrichForm({
  codigo,
  enrich,
  onSaved,
}: {
  codigo: string
  enrich: ProdutoDetalheData['enrich']
  onSaved: () => void
}) {
  // Template do dicionário canônico (ADR-0024 Onda 3) dirige campos, tipos, enums
  // e ordem. Fallback estático cobre o intervalo até carregar (ou erro de rede).
  const { data: template } = useQuery({
    queryKey: ['dicionario-template'],
    queryFn: buscarDicionarioTemplate,
    staleTime: 10 * 60 * 1000,
  })
  const tpl = template && template.length ? template : FALLBACK_TEMPLATE
  const tplColunas = tpl.filter((e) => e.destino === 'coluna')
  const tplFicha = tpl.filter((e) => e.destino === 'ficha')

  const init = (): EnrichInput => ({
    principio_ativo: enrich?.principio_ativo ?? null,
    concentracao: enrich?.concentracao ?? null,
    forma_farmaceutica: enrich?.forma_farmaceutica ?? null,
    via_administracao: enrich?.via_administracao ?? null,
    apresentacao: enrich?.apresentacao ?? null,
    categoria: enrich?.categoria ?? null,
    subcategoria: enrich?.subcategoria ?? null,
    especie: enrich?.especie ?? null,
    especie_alvo: enrich?.especie_alvo ?? null,
    fase_vida: enrich?.fase_vida ?? null,
    porte: enrich?.porte ?? null,
    faixa_qualidade: enrich?.faixa_qualidade ?? null,
    marca: enrich?.marca ?? null,
    fabricante: enrich?.fabricante ?? null,
    peso_min: enrich?.peso_min ?? null,
    peso_max: enrich?.peso_max ?? null,
    volume_valor: enrich?.volume_valor ?? null,
    volume_unidade: enrich?.volume_unidade ?? null,
  })
  // Ficha técnica: valores string viram campos editáveis; o resto (_fontes e
  // eventuais não-string) é preservado intacto e devolvido no save.
  const initFicha = (): Record<string, string> => {
    const o: Record<string, string> = {}
    for (const [k, v] of Object.entries(enrich?.ficha_tecnica ?? {})) {
      if (k !== '_fontes' && typeof v === 'string') o[k] = v
    }
    return o
  }
  const initFichaExtras = (): Record<string, unknown> => {
    const o: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(enrich?.ficha_tecnica ?? {})) {
      if (k === '_fontes' || typeof v !== 'string') o[k] = v
    }
    return o
  }
  const [form, setForm] = useState<EnrichInput>(init)
  const [fichaObj, setFichaObj] = useState<Record<string, string>>(initFicha)
  const [mostrarVazios, setMostrarVazios] = useState(false)
  const [fichaErro, setFichaErro] = useState<string | null>(null)

  useEffect(() => {
    setForm(init())
    setFichaObj(initFicha())
    setFichaErro(null)
  }, [enrich]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string) => (v: string) =>
    setForm((f) => ({ ...f, [k]: v.trim() === '' ? null : v }) as EnrichInput)
  const setNum = (k: string) => (v: number | null) =>
    setForm((f) => ({ ...f, [k]: v }) as EnrichInput)
  const getStr = (k: string): string => {
    const v = (form as Record<string, unknown>)[k]
    return v == null ? '' : String(v)
  }
  const getNum = (k: string): number | null => {
    const v = (form as Record<string, unknown>)[k]
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  const setFicha = (k: string) => (v: string) => setFichaObj((o) => ({ ...o, [k]: v }))

  // Monta o jsonb final da ficha: campos não-vazios + extras preservados, chaves ordenadas
  // (ordem estável p/ comparação de dirty).
  const buildFicha = (obj: Record<string, string>, extras: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(obj).sort()) {
      const v = (obj[k] ?? '').trim()
      if (v !== '') out[k] = obj[k]
    }
    for (const k of Object.keys(extras).sort()) out[k] = extras[k]
    return out
  }

  const mut = useMutation({
    mutationFn: () => {
      // Envia SÓ os campos alterados (Opção A do ADR-0024): o gate marca como ✎ manual
      // (e tombstona vazio) cada campo presente no body — mandar o form inteiro
      // converteria/congelaria campos que o humano nem tocou.
      const baseline = init()
      const payload: Record<string, unknown> = {}
      for (const k of Object.keys(form)) {
        const atual = (form as Record<string, unknown>)[k] ?? null
        const antes = (baseline as Record<string, unknown>)[k] ?? null
        if (JSON.stringify(atual) !== JSON.stringify(antes)) payload[k] = atual
      }
      const fichaNova = buildFicha(fichaObj, initFichaExtras())
      const fichaAntes = buildFicha(initFicha(), initFichaExtras())
      if (JSON.stringify(fichaNova) !== JSON.stringify(fichaAntes)) payload.ficha_tecnica = fichaNova
      if (Object.keys(payload).length === 0) {
        throw new Error('Nenhum campo alterado — nada a salvar.')
      }
      return salvarEnrich(codigo, payload as EnrichInput)
    },
    onSuccess: () => { setFichaErro(null); onSaved() },
    onError: (e) => setFichaErro(String((e as Error).message)),
  })

  // ✦ IA: pesquisa fontes externas (ficha do fabricante + catálogo ML por EAN),
  // sugere os campos e pré-preenche o form (humano revisa antes de salvar).
  const ia = useMutation({
    mutationFn: () => sugerirMedicamentoIA(codigo),
    onSuccess: (s) => {
      setForm((f) => ({
        ...f,
        principio_ativo: s.principio_ativo ?? f.principio_ativo ?? null,
        concentracao: s.concentracao ?? f.concentracao ?? null,
        forma_farmaceutica: s.forma_farmaceutica ?? f.forma_farmaceutica ?? null,
        via_administracao: s.via_administracao ?? f.via_administracao ?? null,
        apresentacao: s.apresentacao ?? f.apresentacao ?? null,
        fabricante: s.fabricante ?? f.fabricante ?? null,
        especie: s.especie ?? f.especie ?? null,
      }))
      if (s.ficha && Object.keys(s.ficha).length > 0) {
        setFichaObj((prev) => {
          const add: Record<string, string> = {}
          for (const [k, v] of Object.entries(s.ficha)) if (v != null) add[k] = String(v)
          return { ...prev, ...add }
        })
      }
    },
  })

  // Score de completude dirigido pelo template (colunas e ficha contadas à parte).
  const colPreenchidas = tplColunas.filter((e) => {
    const v = (form as Record<string, unknown>)[e.coluna_fisica ?? '']
    return v != null && String(v).trim() !== ''
  }).length
  const fichaPreenchidas = tplFicha.filter((e) => (fichaObj[e.chave] ?? '').trim() !== '').length

  // Resumo das fontes consultadas pela última sugestão de IA
  const fontesIa = ia.data?.fontes
  const fontesResumo = fontesIa
    ? [
        fontesIa.fabricante.encontrado
          ? `ficha do fabricante (${fontesIa.fabricante.total_pares} pares)`
          : fontesIa.fabricante.consultado
            ? 'fabricante: não achou'
            : null,
        fontesIa.ml_catalogo.encontrado
          ? `ML por EAN (${fontesIa.ml_catalogo.total_atributos} atributos)`
          : fontesIa.ml_catalogo.consultado
            ? 'ML por EAN: não achou'
            : null,
        fontesIa.web_search?.usado ? 'pesquisa web (bula/fabricante)' : null,
      ].filter(Boolean)
    : null

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          <span className="text-purple-700">Passo 1 · </span>
          Enriquecimento (medicamento · taxonomia · ficha)
        </h2>
        <div className="flex items-center gap-3">
          <span
            title="Score de completude dirigido pelo dicionário de atributos (ADR-0024)"
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"
          >
            campos {colPreenchidas}/{tplColunas.length} · ficha {fichaPreenchidas}/{tplFicha.length}
          </span>
          <ProvenanceBadge enrich={enrich} />
          <button
            type="button"
            onClick={() => ia.mutate()}
            disabled={ia.isPending}
            className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-800 font-medium px-3 py-1 rounded-full transition disabled:opacity-50"
          >
            {ia.isPending ? 'gerando…' : '✦ Sugerir com IA'}
          </button>
          {ia.isError && <span className="text-xs text-red-600">IA falhou</span>}
          {mut.isSuccess && <span className="text-xs text-green-600">✓ salvo</span>}
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Fonte de verdade dos fatos do produto (lido pelo bot e pelo Mercado Livre). Salvar marca como
        <span className="font-semibold"> curadoria humana</span> — a IA não sobrescreve mais.
        <span className="block mt-1 text-purple-700">
          Faça isto <span className="font-semibold">antes</span> da descrição rica (Passo 2): a descrição ancora a redação nos fatos curados aqui.
        </span>
      </p>

      {ia.isSuccess && (
        <p className="text-[11px] text-purple-700 -mt-2 mb-4">
          {fontesResumo && fontesResumo.length > 0
            ? <>Fontes consultadas: {fontesResumo.join(' · ')}</>
            : <>Somente conhecimento do modelo (sem marca mapeada / EAN)</>}
        </p>
      )}

      {/* Colunas estruturadas — dirigidas pelo template do dicionário (tipo/enum/ordem) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tplColunas.map((e) => {
          const k = e.coluna_fisica ?? e.chave
          if (e.tipo === 'numero') {
            return <NumberInput key={e.chave} label={e.rotulo} value={getNum(k)} onChange={setNum(k)} />
          }
          if (e.enum_valores && e.enum_valores.length > 0) {
            return (
              <SelectInput key={e.chave} label={e.rotulo} value={getStr(k)} opcoes={e.enum_valores}
                onChange={set(k)} badge={<FieldBadge f={enrich?.fontes?.[k]} />} />
            )
          }
          return (
            <Input key={e.chave} label={e.rotulo} value={getStr(k)} onChange={set(k)}
              badge={<FieldBadge f={enrich?.fontes?.[k]} />} />
          )
        })}
      </div>

      {/* Ficha técnica — campos tipados do dicionário (fim do JSON cru) */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Ficha técnica <span className="text-slate-400 normal-case">(cauda longa — indicações, composição, uso…)</span>
          </h3>
          <button type="button" onClick={() => setMostrarVazios((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-700 underline">
            {mostrarVazios
              ? 'ocultar campos vazios'
              : `mostrar todos os campos (${tplFicha.length - fichaPreenchidas} vazios)`}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tplFicha
            .filter((e) => mostrarVazios || (fichaObj[e.chave] ?? '').trim() !== '')
            .map((e) =>
              e.tipo === 'booleano' ? (
                <SelectInput key={e.chave} label={e.rotulo} value={fichaObj[e.chave] ?? ''}
                  opcoes={['Sim', 'Não']} onChange={setFicha(e.chave)} />
              ) : (
                <Input key={e.chave} label={e.rotulo} value={fichaObj[e.chave] ?? ''}
                  onChange={setFicha(e.chave)} />
              ),
            )}
          {/* Chaves fora do dicionário (legado): editáveis, removíveis (vazio = remove no save) */}
          {Object.keys(fichaObj)
            .filter((k) => !tplFicha.some((e) => e.chave === k))
            .sort()
            .map((k) => (
              <Input key={`extra-${k}`} label={`${k} (fora do dicionário)`} value={fichaObj[k] ?? ''}
                onChange={setFicha(k)} />
            ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Campo esvaziado é removido da ficha ao salvar. Só os campos <span className="font-semibold">alterados</span> são
          marcados como curadoria manual (✎) — o resto continua aberto a ML/IA.
        </p>
      </div>

      {fichaErro && <p className="text-xs text-red-600 mt-2">{fichaErro}</p>}

      <div className="mt-4">
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {mut.isPending ? 'Salvando…' : 'Salvar revisão (campos alterados viram ✎ manual)'}
        </button>
      </div>

      {mut.isSuccess && mut.data && (
        Object.keys(mut.data).length === 0
          ? <p className="text-xs text-slate-400 mt-3">Nenhum campo alterado.</p>
          : (
            <div className="mt-3 text-xs">
              <p className="text-slate-500 mb-1">{Object.keys(mut.data).length} campo(s) alterado(s):</p>
              <ul className="space-y-0.5">
                {Object.entries(mut.data).map(([campo, { de, para }]) => (
                  <li key={campo} className="text-slate-600">
                    <span className="font-semibold">{campo}</span>:{' '}
                    <span className="text-slate-400 line-through">{fmtDiff(de)}</span>{' → '}
                    <span className="text-green-700">{fmtDiff(para)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )
      )}
    </section>
  )
}

// Formata um valor do diff por-campo para exibição (null/vazio, objeto jsonb, ou texto).
function fmtDiff(v: unknown): string {
  if (v === null || v === undefined || v === '') return '(vazio)'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

type ImagemRow = ProdutoDetalheData['imagens'][number]

const CANAIS_DISPONIVEIS = ['todos', 'web', 'ml', 'shopee', 'whatsapp']
const TIPOS_DISPONIVEIS = ['frontal', 'embalagem', 'lifestyle', 'detalhe', 'ficha_tecnica']

function ImagensBloco({
  codigo,
  imagens,
  onChange,
}: {
  codigo: string
  imagens: ImagemRow[]
  onChange: () => void
}) {
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [arrastando, setArrastando] = useState(false)

  const upload = useMutation({
    mutationFn: async (arquivos: FileList) => {
      setErro(null)
      setEnviando(true)
      try {
        for (const f of Array.from(arquivos)) {
          await uploadImagem(codigo, f, { canais: ['todos'] })
        }
      } finally {
        setEnviando(false)
      }
    },
    onSuccess: onChange,
    onError: (e) => setErro(e instanceof Error ? e.message : String(e)),
  })

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    upload.mutate(files)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setArrastando(false)
    handleFiles(e.dataTransfer.files)
  }

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Imagens ({imagens.length})
        </h2>
        {erro && <span className="text-xs text-red-600">erro: {erro}</span>}
      </div>

      <label
        onDragOver={(e) => {
          e.preventDefault()
          setArrastando(true)
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={onDrop}
        className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
          arrastando ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
        }`}
      >
        <input
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={enviando}
        />
        <p className="text-sm text-slate-600">
          {enviando ? (
            'Enviando…'
          ) : (
            <>
              <span className="font-medium text-blue-700">Clique para enviar</span> ou arraste imagens aqui
            </>
          )}
        </p>
        <p className="text-xs text-slate-400 mt-1">JPG, PNG, WEBP — máx. 20MB cada</p>
      </label>

      {imagens.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
          {imagens.map((img, idx) => (
            <ImagemCard
              key={img.id}
              imagem={img}
              codigo={codigo}
              total={imagens.length}
              indice={idx}
              ids={imagens.map((i) => i.id)}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function ImagemCard({
  imagem,
  codigo,
  total,
  indice,
  ids,
  onChange,
}: {
  imagem: ImagemRow
  codigo: string
  total: number
  indice: number
  ids: number[]
  onChange: () => void
}) {
  const [editando, setEditando] = useState(false)

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof atualizarImagem>[1]) => atualizarImagem(imagem.id, patch),
    onSuccess: () => {
      setEditando(false)
      onChange()
    },
  })

  const del = useMutation({
    mutationFn: () => removerImagem(imagem.id),
    onSuccess: onChange,
  })

  const move = useMutation({
    mutationFn: (direcao: -1 | 1) => {
      const novos = [...ids]
      const j = indice + direcao
      if (j < 0 || j >= novos.length) return Promise.resolve()
      ;[novos[indice], novos[j]] = [novos[j]!, novos[indice]!]
      return reordenarImagens(codigo, novos)
    },
    onSuccess: onChange,
  })

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="aspect-square bg-slate-100 relative">
        <img src={imagem.url} alt={imagem.alt ?? ''} className="w-full h-full object-cover" />
        <span className="absolute top-1.5 left-1.5 bg-slate-900/70 text-white text-[10px] px-1.5 py-0.5 rounded">
          {imagem.ordem}
        </span>
      </div>

      <div className="p-2.5 text-xs space-y-1.5">
        <div className="flex flex-wrap gap-1">
          {imagem.canais.map((c) => (
            <span
              key={c}
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                c === 'todos' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'
              }`}
            >
              {c}
            </span>
          ))}
          {imagem.tipo && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800">
              {imagem.tipo}
            </span>
          )}
        </div>

        <div className="flex justify-between text-[11px] text-slate-500">
          <span>
            {imagem.largura}×{imagem.altura}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => move.mutate(-1)}
              disabled={indice === 0 || move.isPending}
              className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
              aria-label="Mover para cima"
            >
              ↑
            </button>
            <button
              onClick={() => move.mutate(1)}
              disabled={indice === total - 1 || move.isPending}
              className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
              aria-label="Mover para baixo"
            >
              ↓
            </button>
            <button
              onClick={() => setEditando((e) => !e)}
              className="text-blue-600 hover:underline"
            >
              {editando ? 'Cancelar' : 'Editar'}
            </button>
            <button
              onClick={() => {
                if (confirm('Remover essa imagem?')) del.mutate()
              }}
              className="text-red-600 hover:underline"
            >
              Remover
            </button>
          </div>
        </div>

        {editando && (
          <ImagemEditor imagem={imagem} onSave={(p) => save.mutate(p)} disabled={save.isPending} />
        )}
      </div>
    </div>
  )
}

function ImagemEditor({
  imagem,
  onSave,
  disabled,
}: {
  imagem: ImagemRow
  onSave: (p: { canais: string[]; tipo: string | null; alt: string | null }) => void
  disabled: boolean
}) {
  const [canais, setCanais] = useState<string[]>(imagem.canais)
  const [tipo, setTipo] = useState<string | null>(imagem.tipo)
  const [alt, setAlt] = useState<string>(imagem.alt ?? '')

  const toggleCanal = (c: string) => {
    if (canais.includes(c)) {
      setCanais(canais.filter((x) => x !== c))
    } else {
      // Marcar 'todos' desmarca os outros, e vice-versa
      const novos = c === 'todos' ? ['todos'] : [...canais.filter((x) => x !== 'todos'), c]
      setCanais(novos)
    }
  }

  return (
    <div className="space-y-2 pt-2 border-t border-slate-100">
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Canais</p>
        <div className="flex flex-wrap gap-1">
          {CANAIS_DISPONIVEIS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => toggleCanal(c)}
              className={`px-2 py-0.5 rounded text-[10px] border ${
                canais.includes(c)
                  ? 'bg-blue-700 text-white border-blue-700'
                  : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Tipo</p>
        <select
          value={tipo ?? ''}
          onChange={(e) => setTipo(e.target.value || null)}
          className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
        >
          <option value="">— sem tipo —</option>
          {TIPOS_DISPONIVEIS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
          Texto alternativo (acessibilidade)
        </p>
        <input
          type="text"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder="Ex: Frasco de Bimectin 50ml visto de frente"
          className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
        />
      </div>
      <button
        onClick={() => onSave({ canais, tipo, alt: alt.trim() || null })}
        disabled={disabled}
        className="w-full bg-blue-700 hover:bg-blue-800 text-white text-xs py-1.5 rounded disabled:opacity-50"
      >
        {disabled ? 'Salvando…' : 'Salvar'}
      </button>
    </div>
  )
}

// ─── Helpers de UI ───────────────────────────────────────────────────────────
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm text-slate-800">{value}</p>
    </div>
  )
}

function Input({ label, value, onChange, badge }: { label: string; value: string; onChange: (v: string) => void; badge?: React.ReactNode }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-1">
        <span>{label}</span>
        {badge}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
      />
    </div>
  )
}

// Select de vocabulário controlado (enums do dicionário — ADR-0024 Onda 3).
// Opção vazia = limpar o campo. Valor atual fora do enum aparece como opção extra
// (legado), para não sumir silenciosamente da tela.
function SelectInput({
  label,
  value,
  opcoes,
  onChange,
  badge,
}: {
  label: string
  value: string
  opcoes: string[]
  onChange: (v: string) => void
  badge?: React.ReactNode
}) {
  const lista = value && !opcoes.includes(value) ? [value, ...opcoes] : opcoes
  return (
    <div>
      <label className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-1">
        <span>{label}</span>
        {badge}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
      >
        <option value="">—</option>
        {lista.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  )
}

function NumberInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: number | null
  onChange: (v: number | null) => void
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</label>
      <input
        type="number"
        step="0.01"
        value={value ?? ''}
        onChange={(e) => {
          const n = e.target.value === '' ? null : Number(e.target.value)
          onChange(Number.isFinite(n as number) ? (n as number) : null)
        }}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
      />
    </div>
  )
}

function Textarea({
  label,
  value,
  onChange,
  rows = 4,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  rows?: number
  placeholder?: string
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
      />
    </div>
  )
}

type ProdutoDetalheData = Awaited<ReturnType<typeof buscarProduto>>
